import type { Value } from 'platejs';
import { deserializeMd } from '@platejs/markdown';
import remarkGfm from 'remark-gfm';

// @platejs/markdown returns nested ul/li/lic trees for lists, but our editor
// uses the v53 BlockList indent-based model (top-level <p> blocks with
// listStyleType + indent props). Flatten the tree here so commit's
// insertFragment / insertNodes lands the AI output in the same shape the
// rest of the editor produces — otherwise list nodes render as plain text
// because the editor has no <ul>/<li>/<lic> element renderers registered.
const LIST_TYPE_MAP: Record<string, 'disc' | 'decimal'> = { ul: 'disc', ol: 'decimal' };

function flattenLists(nodes: any[], indent = 0): any[] {
    const out: any[] = [];
    for (const node of nodes) {
        if (node && (node.type === 'ul' || node.type === 'ol')) {
            const listStyleType = LIST_TYPE_MAP[node.type];
            for (const li of node.children ?? []) {
                if (li?.type !== 'li') continue;
                let textChildren: any[] = [];
                const nestedLists: any[] = [];
                for (const child of li.children ?? []) {
                    if (child?.type === 'lic' || child?.type === 'p') {
                        textChildren = textChildren.concat(child.children ?? []);
                    } else if (child?.type === 'ul' || child?.type === 'ol') {
                        nestedLists.push(child);
                    } else {
                        textChildren.push(child);
                    }
                }
                if (textChildren.length === 0) textChildren = [{ text: '' }];
                out.push({ type: 'p', listStyleType, indent: indent + 1, children: textChildren });
                if (nestedLists.length > 0) out.push(...flattenLists(nestedLists, indent + 1));
            }
        } else if (node && Array.isArray(node.children)) {
            out.push({ ...node, children: flattenLists(node.children, indent) });
        } else {
            out.push(node);
        }
    }
    return out;
}

// Post-processing pass applied to every node tree produced by deserializeMd.
// Fixes three structural issues before the fragment reaches the editor:
//
// 1. img→image: @platejs/markdown's img deserializer calls getPluginType with
//    KEYS.img ('img'). Our editor registers the image plugin under key 'image',
//    not 'img', so getPluginType falls back to 'img'. Since there is no 'img'
//    plugin/renderer, those nodes are silently dropped. Remap them to 'image'
//    with the required void children marker.
//
// 2. h4/h5/h6 clamp: the editor only registers H1/H2/H3 plugins. Headings at
//    mdast depth 4-6 deserialize as type 'h4'/'h5'/'h6', which have no
//    renderer → silent drop (taxonomy #6). Collapse them to h3.
//
// 3. blockquote: Plate's BlockquotePlugin.normalizeNode already wraps inline
//    children into p blocks, so the { type:'blockquote', children:[{type:'p'}] }
//    shape produced by deserializeMd is valid and handled by the normalizer.
//    No post-processing needed here.
function postProcess(nodes: any[]): any[] {
    const out: any[] = [];
    for (const node of nodes) {
        if (node == null) continue;

        // Leaf — pass through unchanged.
        if (typeof node.text === 'string') {
            out.push(node);
            continue;
        }

        const type: string | undefined = node.type;

        // Fix 1: markdown img → editor image (void node, requires marker child).
        // The markdown plugin also splits paragraphs at img boundaries, so images
        // arrive as top-level siblings with type 'img'. Ensure the void marker child
        // is present regardless of what deserializeMd put in children.
        if (type === 'img') {
            out.push({
                ...node,
                type: 'image',
                children: [{ text: '' }],
            });
            continue;
        }

        // Fix 2: clamp deep headings to h3 (the deepest registered heading plugin).
        if (type === 'h4' || type === 'h5' || type === 'h6') {
            const children = Array.isArray(node.children) ? postProcess(node.children) : [{ text: '' }];
            out.push({ ...node, type: 'h3', children });
            continue;
        }

        // Recurse into children for all other element nodes.
        if (Array.isArray(node.children)) {
            out.push({ ...node, children: postProcess(node.children) });
        } else {
            out.push(node);
        }
    }
    return out;
}

// Convert an AI-streamed markdown string to a Plate node tree shaped for our
// editor. Uses @platejs/markdown's MDAST-based deserializer with the GFM
// extension (tables / strikethrough / autolinks), then post-processes the
// result through flattenLists to match our v53 BlockList model.
//
// Requires an editor instance so deserializeMd can route block-/leaf-level
// nodes through the editor's plugin set (basic-nodes for headings/marks,
// TablePlugin for table sub-trees, LinkPlugin for `a` etc.).
export function markdownToPlate(editor: any, md: string): Value {
    if (!md) return [{ type: 'p', children: [{ text: '' }] }] as Value;
    let nodes: any[];
    try {
        nodes = deserializeMd(editor, md, { remarkPlugins: [remarkGfm] }) as any[];
    } catch {
        // deserializeMd failed entirely — split the raw markdown into separate
        // paragraph blocks rather than packing all lines (including newlines) into
        // one leaf, which would embed literal '\n' characters in the editor content.
        const lines = md.split('\n');
        const blocks = lines
            .map((line) => ({ type: 'p', children: [{ text: line }] }))
            .filter((block) => block.children[0].text.length > 0 || lines.length === 1);
        // All-blank input (e.g. '\n\n') filters to nothing — return a single empty
        // paragraph so we never hand back an invalid empty Plate Value, matching
        // the success-path guard below.
        return (blocks.length ? blocks : [{ type: 'p', children: [{ text: '' }] }]) as Value;
    }
    const processed = postProcess(nodes ?? []);
    const flat = flattenLists(processed);
    if (flat.length === 0) return [{ type: 'p', children: [{ text: '' }] }] as Value;
    return flat as Value;
}
